/**
 * English translations (source of truth)
 *
 * All other language files must match this structure exactly.
 * Use flat-ish keys with feature prefixes for organization.
 */
export const en = {
  // Common UI elements
  common: {
    appName: 'Dashboard',
    loading: 'Loading...',
    loadingDashboard: 'Loading Dashboard...',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    adding: 'Adding...',
    create: 'Create',
    update: 'Update',
    submit: 'Submit',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
    all: 'All',
    none: 'None',
    search: 'Search',
    filter: 'Filter',
    filters: 'Filters',
    reset: 'Reset',
    refresh: 'Refresh',
    download: 'Download',
    upload: 'Upload',
    view: 'View',
    details: 'Details',
    actions: 'Actions',
    status: 'Status',
    date: 'Date',
    time: 'Time',
    name: 'Name',
    email: 'Email',
    description: 'Description',
    notes: 'Notes',
    type: 'Type',
    user: 'User',
    role: 'Role',
    organization: 'Organization',
    required: 'Required',
    optional: 'Optional',
    notAvailable: 'N/A',
    other: 'Other',
    goBack: 'Go Back',
  },

  // User roles (used in profile page and elsewhere)
  roles: {
    admin: 'Admin',
    pilot: 'Pilot',
    maintenance: 'Maintenance',
    airspace_observer: 'Airspace Observer',
  },

  // User Profile
  profile: {
    myProfile: 'My Profile',
    userProfile: 'User Profile',
    loadError: 'Failed to load profile',
    notFound: 'Profile not found',
    bio: 'Bio',
    bioPlaceholder: 'Tell us about yourself...',
    noBio: 'No bio added yet.',
    bioSaved: 'Bio saved successfully',
    bioSaveError: 'Failed to save bio',
    fullName: 'Full Name',
    noName: 'No name set',
    nameSaved: 'Name saved successfully',
    nameSaveError: 'Failed to save name',
    certifications: 'Certifications',
    noCertifications: 'No certifications added yet.',
    addCertification: 'Add Certification',
    certificationName: 'Certification Name',
    expiryDate: 'Expiry Date',
    uploadDocument: 'Upload Document',
    certificationAdded: 'Certification added successfully',
    certificationAddError: 'Failed to add certification',
    certificationRemoved: 'Certification removed successfully',
    certificationRemoveError: 'Failed to remove certification',
    expired: 'Expired',
    expiresOn: 'Expires',
    deletePicture: 'Delete Picture',
    pictureUploaded: 'Profile picture uploaded successfully',
    pictureUploadError: 'Failed to upload profile picture',
    pictureDeleted: 'Profile picture deleted successfully',
    pictureDeleteError: 'Failed to delete profile picture',
    memberSince: 'Member since',
    totalPilotHours: 'Total Flight Hours as Pilot',
  },

  // Authentication
  auth: {
    signIn: 'Sign In',
    signOut: 'Sign Out',
    signingOut: 'Signing Out...',
    loginSuccessful: 'Login successful',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    fullName: 'Full Name',
    forgotPassword: 'Forgot password?',
    backToLogin: 'Back to Login',
    authenticationRequired: 'Authentication Required',
    invitationOnly:
      'Access is by invitation only. Ask an administrator of your organization to invite you.',

    // Validation messages
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email address',
    passwordRequired: 'Password is required',
    passwordMinLength: 'Password must be at least 6 characters long',
    passwordsDoNotMatch: 'Passwords do not match',
    fullNameRequired: 'Full name is required',

    // Success messages

    // Error messages
    pendingApproval:
      'Your account is pending approval. Please contact an administrator to be assigned to an organization before you can sign in.',
  },

  // Password Reset
  passwordReset: {
    title: 'Reset Password',
    verifyCode: 'Verify Code',
    setNewPassword: 'Set New Password',
    emailPrompt: "Enter your email address and we'll send you a verification code.",
    codeSentTo: 'Enter the 6-digit code sent to {{email}}',
    newPasswordPrompt: 'Enter your new password below.',
    sendVerificationCode: 'Send Verification Code',
    verificationCode: 'Verification Code',
    codeHelperText: 'Enter the 6-digit code from your email',
    verifyCodeButton: 'Verify Code',
    resendCode: 'Resend Code',
    resendCodeIn: 'Resend code in {{seconds}}s',
    changeEmail: 'Change Email',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    passwordHelperText: 'Must be at least 6 characters',
    resetPasswordButton: 'Reset Password',
    updatingPassword: 'Updating Password...',

    // Success state
    successTitle: 'Password Reset Successful!',
    successMessage:
      'Your password has been updated successfully. You can now sign in with your new password.',
    redirecting: 'You will be redirected to the login page shortly...',

    // Updated process page
    processUpdatedTitle: 'Password Reset Updated',
    processUpdatedMessage:
      'Our password reset process has been updated. We now use verification codes instead of magic links for better security and compatibility.',
    instructionsTitle: 'To reset your password:',
    instruction1: 'Click the button below to go to the password reset page',
    instruction2: 'Enter your email address',
    instruction3: 'Enter the 6-digit verification code sent to your email',
    instruction4: 'Set your new password',
    goToPasswordReset: 'Go to Password Reset',
    autoRedirect: 'You will be automatically redirected in 5 seconds...',

    // Validation
    codeRequired: 'Verification code is required',
    codeMustBe6Digits: 'Verification code must be 6 digits',
    invalidCode: 'Invalid or expired verification code',

    // Toast messages
    codeSent: 'Verification code sent to your email.',
    codeResent: 'Verification code resent to your email.',
    codeVerified: 'Code verified successfully!',
    passwordUpdated: 'Password updated successfully!',
  },

  // Accept Invitation
  invitation: {
    validating: 'Validating invitation...',
    invalidTitle: 'Invalid Invitation',
    invalidMessage:
      'This invitation may have expired or already been used. Please contact your organization administrator to request a new invitation.',
    goToLogin: 'Go to Login',
    acceptTitle: 'Accept Invitation',
    invitedTo: "You've been invited to join {{organization}}",
    enterFullName: 'Enter your full name',
    createAccountButton: 'Create Account & Join Organization',
    creatingAccount: 'Creating Account...',
    successTitle: 'Account Created Successfully!',
    accountCreated: 'Account created successfully. You can now sign in.',
    welcomeMessage:
      'Welcome to {{organization}}! Your account is ready. You will be redirected to the login page shortly.',
    redirectingToLogin: 'You will be redirected to the login page shortly...',
    alreadyHaveAccount: 'Already have an account?',
    signInHere: 'Sign in here',
    noToken: 'No invitation token provided',
    invalidData: 'Invalid invitation data',
    userCreationFailed: 'Account created but user ID not returned. Please try signing in.',
  },

  // No Organization Screen
  noOrganization: {
    title: 'Account Pending Approval',
    message:
      'Your account has been created successfully! Please contact an administrator to be assigned to an organization before you can access the maintenance panel.',
    loggedInAs: 'Logged in as: {{email}}',
  },

  // Dashboard Header
  header: {
    plotFlightData: 'Plot Flight Data',
    adminTools: 'Admin Tools',
  },

  // Dashboard Tabs
  tabs: {
    overview: 'Overview',
    files: 'Files',
    profile: 'Profile',
    maintenance: 'Maintenance',
    healthPanel: 'Health Panel',
    inventory: 'Inventory',
    calendar: 'Calendar',
  },

  // Filter Dialog
  filterDialog: {
    fileFilters: 'File Filters',
    droneSerial: 'Drone Serial',
    startDate: 'Start Date',
    endDate: 'End Date',
    resetFileFilters: 'Reset File Filters',
    sortOptions: 'Sort Options',
    sortBy: 'Sort By',
    sortByName: 'Name',
    sortByDate: 'Date',
    sortBySerial: 'Drone Serial',
    ascending: 'Asc',
    descending: 'Desc',
  },

  // Overview Tab
  overview: {
    fleetOverview: 'Fleet Overview',
    fleetSummary: 'Fleet Summary',
    totalDrones: 'Total Drones',
    totalEngineHours: 'Total Engine Hours',
    totalFlightHours: 'Total Flight Hours',
    allDrones: 'All Drones',
    clickToViewProfile: 'Click on any drone card to view drone profile',
    noDronesFound: 'No drones found for your organization',
    latestFlights: 'Latest Flights',
    noFlightsRecorded: 'No flights recorded yet',
    failedToLoadFlights: 'Failed to load latest flights',
    drone: 'Drone',
    maintenance: 'Maintenance',
    flightHours: 'Flight Hours',
    engineHours: 'Engine Hours',
    overdue: 'OVERDUE: {{hours}}h',
    next: 'Next: {{hours}}h',
    // Personnel Overview
    personnel: {
      title: 'Personnel Overview',
      pilots: 'Pilots',
      maintenanceEngineers: 'Maintenance Engineers',
      noPilots: 'No pilots found in your organization',
      noMaintenanceEngineers: 'No maintenance engineers found in your organization',
      unnamed: 'Unnamed',
      roles: {
        pilot: 'Pilot',
        maintenance: 'Maintenance',
      },
    },
  },

  // Drone Status
  droneStatus: {
    airworthy: 'Airworthy',
    airworthyTooltip: 'Drone is airworthy and ready for operations',
    airworthyWithLimitations: 'Airworthy with limitations',
    airworthyWithLimitationsTooltip: 'Drone is airworthy but has operational limitations',
    maintenance: 'Maintenance',
    maintenanceTooltip: 'Drone is currently under maintenance',
    notAirworthy: 'Not Airworthy',
    notAirworthyTooltip: 'Drone is not airworthy and cannot be operated',
    withCustomer: 'With customer',
    withCustomerTooltip: 'Drone is currently with a customer',
    inProduction: 'In production',
    inProductionTooltip: 'Drone is being manufactured or assembled and is not yet in service',
    failedToUpdate: 'Failed to update drone status',
  },

  // Drone Picture
  dronePicture: {
    failedToUpload: 'Failed to upload drone picture',
  },

  // Profile Tab
  profileTab: {
    selectDrone: 'Select a drone to view statistics',
    droneInformation: 'Drone Information',
    plotData: 'Plot Data',
    totalFiles: 'Total Files',
    firstFlight: 'First Flight',
    firstEngineRun: 'First Engine Run',
    engineStarts: 'Engine Starts',
    engineHours: 'Engine Hours',
    flightHours: 'Flight Hours',
    // #1612: the definition shown under the figure on the drone page.
    flightHoursDefinition:
      "Airborne time, from the flight logger's in-flight flag (generator RPM above 5,100 where a log has none).",
    hoursBreakdown: '({{tracked}} tracked + {{legacy}} legacy)',
    droneStatusLabel: 'Drone Status',
    statusUpdated: 'Drone status updated to {{status}}',
    invalidDate: 'Invalid Date',
    ownershipLabel: 'Ownership',
    ownershipSold: 'Sold',
    ownershipLeased: 'Leased',
    leaseEnds: 'ends',
    failedToLoadStatus: 'Failed to load drone status',
  },

  // #1693: drone pages show own + shared flights, each shared one attributed.
  dronePageShared: {
    sharedBy: 'Shared by {{org}}',
    sharedByUnknown: 'Shared by another organization',
    countersIncludeSharedOne: 'Includes {{count}} flight shared by {{orgs}}',
    countersIncludeSharedOther: 'Includes {{count}} flights shared by {{orgs}}',
    timelineShared: 'Includes shared flights: {{orgs}}',
    trendPointShared: 'Shared by {{org}}',
  },

  // Technical Specifications
  technicalSpecs: {
    title: 'Technical Specifications',
    subtitle: 'Configuration, Software, Serial Numbers, and Options',
    configuration: 'Configuration',
    software: 'Software',
    serialNumbers: 'Serial Numbers',
    options: 'Options',
    updatedSuccessfully: '{{field}} updated successfully',
    failedToUpdate: 'Failed to update technical specification',
    tempSensorCableClip: "Temp sensor cable's fixing clip",
    notSet: 'Not set',
  },

  // Notes
  notes: {
    title: 'Notes',
    notesFor: 'Notes for {{serial}}',
    summary: '{{total}} total • {{unresolved}} unresolved • {{resolved}} resolved',
    filters: 'Filters',
    createNote: 'Create Note',
    search: 'Search',
    type: 'Type',
    allTypes: 'All Types',
    general: 'General',
    customerFeedback: 'Customer Feedback',
    alert: 'Alert',
    priority: 'Priority',
    allPriorities: 'All Priorities',
    information: 'Information',
    critical: 'Critical',
    includeResolved: 'Include Resolved',
    selectDroneToView: 'Select a drone to view notes',
    noNotesFound: 'No notes found',
    createFirstNote: 'Create your first note',
    addNote: 'add note',
    failedToLoad: 'Failed to load notes',
    failedToCreate: 'Failed to create note',
    failedToUpdate: 'Failed to update note',
    failedToDelete: 'Failed to delete note',
    failedToResolve: 'Failed to resolve note',
    failedToUnresolve: 'Failed to unresolve note',
  },

  // Maintenance Tab
  maintenanceTab: {
    selectDrone: 'Select a drone to view maintenance records',
    upcomingMaintenance: 'Upcoming Maintenance',
    addMaintenance: 'Add Maintenance',
    maintenanceHistory: 'Maintenance History',
    componentMaintenanceSchedule: 'Component Maintenance Schedule',
    visualInspectionSchedule: 'Visual Inspection Maintenance Schedule',
    noUpcoming: 'No upcoming maintenance scheduled',
    noHistory: 'No maintenance history available',

    // Schedule card
    overdue: 'OVERDUE',
    dueIn: 'Due in:',
    hrsOverdue: '{{hours}} hrs overdue',
    hrs: '{{hours}} hrs',
    trackingOnly: 'Tracking only',
    hrsAccumulated: '{{hours}} hrs accumulated',
    viewMaintenanceManual: 'View maintenance manual',
    viewInspectionInstructions: 'View inspection instructions',

    // Historical/Record card
    performedBy: 'Performed by:',
    addedBy: 'Added by:',
    reviewedBy: 'Reviewed by:',
    qualityChecked: 'Quality Checked',
    workOrderId: 'Work Order ID:',
    unknown: 'Unknown',

    // Record statuses
    statusCompleted: 'Completed',
    statusCancelled: 'Cancelled',
    statusPending: 'Pending',

    // View dialog
    workDescription: 'Work Description',
    qualityCheckedBy: 'Quality Checked by:',
    deleteMaintenanceRecord: 'Delete Maintenance Record',
    deleteConfirmMessage: 'Are you sure you want to delete this maintenance record?',
    deleteRecalculationWarning:
      'This action will trigger automatic recalculation of maintenance counters for drone',
    deleteSuccess: 'Successfully deleted maintenance record',
    deleteFailed: 'Failed to delete maintenance record. Please try again.',

    // Record form
    startDate: 'Start date:',
    endDate: 'End date:',
    startDateLabel: 'Start Date',
    endDateLabel: 'End Date',
    maintenancePerformed: 'Maintenance performed',
    unknownUser: 'Unknown User',
    differentUserRequired: '(Different user required)',
    failedToOpenManual: 'Failed to open maintenance manual',
    failedToLoadTimeline: 'Failed to load maintenance timeline',
    failedToDownloadAttachment: 'Failed to download maintenance attachment',
  },

  // Statistics mutations
  statistics: {
    failedToFetchWeather: 'Failed to fetch weather data',
    failedToUpdateFlight: 'Failed to update flight metadata',
    failedToUploadAttachment: 'Failed to upload attachment',
  },

  // Files Tab
  filesTab: {
    title: 'Files',
    uploadFile: 'Upload File',
    noFiles: 'No files found',
    fileName: 'File Name',
    fileSize: 'Size',
    uploadedBy: 'Uploaded By',
    uploadedAt: 'Uploaded At',
    downloadFile: 'Download File',
    deleteFile: 'Delete File',
    confirmDelete: 'Are you sure you want to delete this file?',
  },

  // File Management
  fileManagement: {
    title: 'File Management',
    root: 'Root',
    // STD-029 B-4. The browser renders a folder somebody else shared, so the
    // breadcrumb has to say whose tree you are standing in — there is no Home
    // crumb from inside a share, and the way out is back to the Shared view.
    sharedFolderChip: 'Shared with you',
    backToShared: 'Back to Shared',
    // STD-029 B-5 — a folder url that leads nowhere. ONE message covers absent,
    // deleted and not-visible-to-you, because the API answers 404 for all three
    // on purpose: a distinct "you may not see this" would confirm the folder is
    // real. The file case is separate only because the caller can demonstrably
    // see that row already.
    folderNotFoundTitle: 'Folder not found',
    folderNotFoundBody:
      'This link does not lead to a folder you can open. It may have been deleted, or it may never have been shared with your organization.',
    notAFolderTitle: 'That link points at a file',
    notAFolderBody:
      'Folder links open a folder. This one names a file, so there is nothing to list — open it from the folder it lives in.',
    backToMyFiles: 'Back to my files',
    // A folder card's server-computed aggregates. Over the FILTERED subtree
    // when a filter is active, so the count agrees with what opening it shows.
    folderFileCount: '{{count}} files',
    folderSerial: 'Drone {{serial}}',
    // Growth past the page bound, made visible rather than silent.
    showingOfTotal: 'Showing {{shown}} of {{total}}',
    newFolder: 'New Folder',
    uploadFiles: 'Upload Files',
    hideUpload: 'Hide Upload',
    documents: 'Documents',
    noFilesInDirectory: 'No files found in this directory',
    moreActions: 'More actions',

    // Context menu
    download: 'Download',
    downloadMany: 'Download ({{count}} files)',
    bulkDownloadProgress: 'Downloading {{done}} of {{total}}...',
    bulkDownloadBrowserHint:
      'Your browser may ask once whether this site may download several files.',
    bulkDownloadDone: 'Started {{count}} downloads.',
    bulkDownloadPartial: 'Started {{started}} of {{total}} downloads. Not downloaded: {{names}}',
    rename: 'Rename',
    move: 'Move',
    delete: 'Delete',
    plot: 'Plot',
    details: 'Details',

    // Move mode
    moving: 'Moving: {{fileName}}',
    moveInstructions: 'Navigate to the destination folder and click "Move here"',
    moveHere: 'Move here',

    // Create folder dialog
    createNewFolder: 'Create New Folder',
    folderName: 'Folder Name',
    folderNameInvalidDots: "Folder name cannot contain '..' (security risk)",
    folderNameInvalidStart: 'Folder name cannot start with path separators',
    folderNameInvalidSlash: "Folder name cannot contain forward slashes ('/')",

    // Rename folder dialog
    renameFolderTitle: 'Rename Folder',
    newFolderName: 'New folder name',

    // Upload zone
    uploadingTo: 'Uploading to: {{path}}',

    // Analysis results
    flightAnalysisResults: 'Flight Analysis Results',
    rowsProcessed: 'Rows Processed',
    columnsProcessed: 'Columns Processed',
    extractedMetadata: 'Extracted Metadata',

    // File uploader
    dropFilesHere: 'Drop files here',
    dragAndDropFiles: 'Drag & drop files here',
    orClickToBrowse: 'or click to browse files',
    allowedFilesInfo: 'Allowed: {{types}} (Max: {{maxFiles}} files)',
    selectedFiles: 'Selected Files ({{count}})',
    analyze: 'Analyze',
    remove: 'Remove',
    uploadingProgress: 'Uploading... {{progress}}%',
    clearAll: 'Clear All',

    // Upload settings dialog
    uploadSettings: 'Upload Settings',
    destination: 'Destination:',
    destinationHelp:
      'Files will be uploaded to the folder you are currently browsing. Navigate to your target folder before uploading.',
    flightTypeRequired: 'Flight Type *',
    loadingFlightTypes: 'Loading flight types...',
    selectFlightType: 'Select flight type',
    payloadRequired: 'Payload *',
    loadingPayloads: 'Loading payloads...',
    selectPayload: 'Select payload',
    pilotRequired: 'Pilot *',
    loadingPilots: 'Loading pilots...',
    selectPilot: 'Select pilot',
    customer: 'Customer',
    loadingCustomers: 'Loading customers...',
    selectCustomer: 'Select customer',
    addCustomer: 'Add new customer',
    addNewCustomer: 'Add New Customer',
    customerName: 'Customer name',
    customerAdded: 'Customer added successfully',
    failedToAddCustomer: 'Failed to add customer',
    project: 'Project',
    loadingProjects: 'Loading projects...',
    selectProject: 'Select project',
    addProject: 'Add new project',
    addNewProject: 'Add New Project',
    projectName: 'Project name',
    projectAdded: 'Project added successfully',
    failedToAddProject: 'Failed to add project',
    addPayload: 'Add new payload',
    addNewPayload: 'Add New Payload',
    payloadName: 'Payload name',
    payloadWeight: 'Weight (kg)',
    payloadAdded: 'Payload added successfully',
    failedToAddPayload: 'Failed to add payload',
    airspaceObserver: 'Airspace Observer',
    loadingAirspaceObservers: 'Loading airspace observers...',
    selectAirspaceObserver: 'Select airspace observer',
    payloadOperator: 'Payload Operator',
    loadingPayloadOperators: 'Loading payload operators...',
    selectPayloadOperator: 'Select payload operator',
    flightTypePayloadRequired: 'Flight type and payload are required for CSV flight data files',
    flightTypePayloadPilotRequired:
      'Flight type, payload, and pilot are required for CSV flight data files',
    documentFilesInfo: 'Document files will be uploaded directly - no flight metadata required',
    testModeLabel: 'Upload as test data (bypass validation)',
    filesToUpload: 'Files to upload:',
    uploadCount: 'Upload {{count}} File(s)',

    // Success messages
    uploadSuccess: 'Successfully uploaded {{count}} file(s)',
    fileAnalyzed: 'File analyzed successfully',
    fileDeleted: 'File deleted successfully',
    folderDeleted: 'Folder deleted successfully',
    folderCreated: 'Folder created successfully',
    fileMoved: 'File moved successfully',
    itemsMoved: '{{count}} item(s) moved successfully',
    itemsMovedPartial: '{{moved}} moved, {{failed}} failed: {{names}}',
    folderMoved: 'Folder moved successfully',
    folderRenamed: 'Folder renamed successfully',
    downloadStarted: 'Download started',

    // Error messages
    includeUntaggedData: 'Include untagged data',
    failedToLoadFiles: 'Failed to load files',
    failedToUpload: 'Upload failed',
    failedToAnalyze: 'Analysis failed',
    failedToDownload: 'Download failed',
    failedToDelete: 'Failed to delete file',
    failedToDeleteFolder: 'Failed to delete folder',
    failedToMove: 'Failed to move item',
    failedToCreateFolder: 'Failed to create folder',
    failedToRenameFolder: 'Failed to rename folder',
    failedToGenerateUrl: 'Failed to generate download URL',
    cannotMoveToSameLocation: 'Cannot move item to the same location',
    maxFilesAllowed: 'Maximum {{count}} files allowed',
    invalidFileTypes: 'Invalid file types. Allowed: {{types}}',
    selectFilesToUpload: 'Please select files to upload',
    selectFlightTypeForCsv: 'Please select a flight type for CSV files',
    selectPayloadForCsv: 'Please select a payload for CSV files',
    selectPilotForCsv: 'Please select a pilot for CSV files',
    failedToLoadData: 'Failed to load flight types, payloads, and pilots. Please try again.',
    analysisCompleted: 'Analysis completed for {{fileName}}',
    testData: 'Test Data',
    unknownFile: 'Unknown file',

    // Duplicate file handling
    duplicateFilesSkipped: 'Duplicate Files Skipped',
    duplicateFilesMessage:
      'The following {{count}} file(s) were skipped because they already exist:',

    // Reports
    reports: 'Reports',
  },

  // Flight Reports
  reports: {
    title: 'Flight Reports',
    backToFiles: '← Back to Files',
    generateReport: 'Generate Report',
    noReports: 'No Reports Yet',
    generateFirstReport: 'Generate your first flight hours report',

    // Loading/Error states
    failedToLoad: 'Failed to load reports',
    failedToGenerateUrl: 'Failed to generate download URL',
    failedToDownload: 'Failed to download report',
    failedToDelete: 'Failed to delete report',
    failedToLoadFilters: 'Failed to load filter options',

    // Actions
    confirmDelete: 'Are you sure you want to delete "{{title}}"?',
    deleteSuccess: 'Report "{{title}}" deleted successfully',
    deleteFailed: 'Failed to delete report',
    moreActions: 'More actions',
    download: 'Download',
    delete: 'Delete',
    downloadStarted: 'Download started for "{{title}}"',

    // Generation dialog
    generateReportTitle: 'Generate Report',
    reportTitle: 'Report Title',
    titlePlaceholder: 'e.g., Q4 2025 Flight Summary',
    titleRequired: 'Report title is required',
    datesRequired: 'Start and end dates are required',
    invalidDateRange: 'Start date must be before end date',
    startDate: 'Start Date',
    endDate: 'End Date',
    selectDrones: 'Select Drones',
    allDrones: 'All Drones',
    selectPayloads: 'Select Payloads',
    allPayloads: 'All Payloads',
    selectPilots: 'Select Pilots',
    allPilots: 'All Pilots',
    leaveEmptyForAll: 'Leave empty to include all',
    includeIncidents: 'Incidents/Accidents',
    generate: 'Generate Report',
    generateSuccess: 'Report generated successfully',
    generateFailed: 'Failed to generate report',

    // Content type selection
    includeInReport: 'Include in Report',
    includeFlightHours: 'Flight Hours',
    includeMaintenanceData: 'Maintenance Data',
    selectAtLeastOne: 'Select at least one content type to include in the report',

    // Maintenance filters
    maintenanceTypes: 'Maintenance Types',
    allMaintenanceTypes: 'All Maintenance Types',
    maintenanceTypesHelper: 'Leave empty to include all maintenance types',
  },

  // Health Panel Tab
  healthPanel: {
    title: 'Health Panel',
    selectDrone: 'Select a drone to view health panel',
    systemHealth: 'System Health',
    lastUpdated: 'Last Updated',
    noData: 'No health data available',

    // Panel titles
    engineStartup: 'Engine Startup',
    performance: 'Performance',
    cooling: 'Cooling',
    batteries: 'Batteries',
    fuelSystem: 'Fuel System',
    liftSystem: 'Lift System',

    // Panel info content
    performanceInfo:
      'Normal range: 80-100%. Trend calculated by linear regression on historical flight data.',
    coolingInfo:
      'Normal range: 0-125°C. Trend calculated by linear regression on historical flight data.',

    // Analysis displays
    latest: 'Latest {{title}}:',
    trend: 'Trend:',
    forecast: 'Forecast:',
    forecastBy: 'by {{date}}',
    rSquared: '(R² = {{value}})',
    insufficientData: 'Insufficient data for trend analysis (need at least 3 data points)',

    // Click-a-point formula popup
    pointFormula: 'Formula',
    pointValues: "This flight's values",
    pointNoSources: 'Source values were not stored for this flight.',
    pointNoFormula: 'No formula details available for this metric.',

    // Status labels
    statusNormal: 'NORMAL',
    statusWarning: 'WARNING',
    statusUnknown: 'UNKNOWN',
    outOfBandFlights: '{{count}} of {{total}} plotted flights outside the acceptable range',

    // TUWT display
    warningTime: 'Warning Time',
    errorTime: 'Error Time',
    ofTotal: '({{percentage}}% of total)',
    totalMonitoringTime: 'Total Monitoring Time:',

    // Trend labels
    trendImproving: 'IMPROVING',
    trendWorsening: 'WORSENING',
    trendStable: 'STABLE',
    trendLabel: 'Trend: {{trend}}',
  },

  // Admin Tools
  admin: {
    title: 'Admin Tools',
    droneManagement: 'Drone Management',
    userManagement: 'User Management',
    organizationManagement: 'Organization Management',
    announcements: 'Announcements',
    inviteUser: 'Invite User',
    createDrone: 'Create Drone',
    createOrganization: 'Create Organization',
    createAnnouncement: 'Create Announcement',

    // Users
    users: 'Users',
    userName: 'Name',
    userEmail: 'Email',
    userRole: 'Role',
    userOrganization: 'Organization',
    userStatus: 'Status',
    editUser: 'Edit User',
    deleteUser: 'Delete User',
    assignOrganization: 'Assign Organization',

    // Roles
    roleAdmin: 'Admin',
    roleUser: 'User',
    roleMaintainer: 'Maintainer',

    // Drones
    drones: 'Drones',
    droneSerial: 'Serial Number',
    droneOrganization: 'Organization',
    editDrone: 'Edit Drone',
    deleteDrone: 'Delete Drone',

    // Organizations
    organizations: 'Organizations',
    orgName: 'Name',
    orgSlug: 'Slug',
    editOrganization: 'Edit Organization',
    deleteOrganization: 'Delete Organization',

    // Announcements
    announcementTitle: 'Title',
    announcementContent: 'Content',
    announcementPriority: 'Priority',
    priorityLow: 'Low',
    priorityMedium: 'Medium',
    priorityHigh: 'High',
    editAnnouncement: 'Edit Announcement',
    deleteAnnouncement: 'Delete Announcement',
  },

  // Invite User Dialog
  inviteDialog: {
    title: 'Invite User',
    emailLabel: 'Email Address',
    emailPlaceholder: 'Enter email address',
    roleLabel: 'Role',
    sendInvitation: 'Send Invitation',
    sending: 'Sending...',
    successMessage: 'Invitation sent successfully to {{email}}',
    errorMessage: 'Failed to send invitation',
  },

  // Inbox Modal
  inbox: {
    title: 'Inbox',
    noMessages: 'No messages',
    markAsRead: 'Mark as Read',
    markAllAsRead: 'Mark All as Read',
    delete: 'Delete',
    failedToLoad: 'Failed to load announcements',
    failedToMarkRead: 'Failed to mark announcement as read',
    failedToDownloadAttachment: 'Failed to download attachment',
  },

  // Documents View
  documents: {
    title: 'Documents',
    backToFiles: '← Back to Files',
    uploadDocument: 'Upload Document',
    noDocuments: 'No documents found',
    uploadFirstDocument: 'Upload your first document to get started',
    moreActions: 'More actions',
    download: 'Download',
    delete: 'Delete',
    validUntil: 'Valid until:',

    // Document types
    typeInsurance: 'Insurance',
    typePermit: 'Permit',
    typeOther: 'Other',

    // Upload dialog
    selectFile: 'Select File',
    titleLabel: 'Title',
    descriptionLabel: 'Description',
    documentType: 'Document Type',
    validityStartDate: 'Validity Start Date (Optional)',
    validityEndDate: 'Validity End Date (Optional)',

    // Validation
    titleRequired: 'Title is required',
    pleaseSelectFile: 'Please select a file',
    startDateBeforeEnd: 'Start date must be before end date',

    // Success/Error messages
    uploadSuccess: 'Document uploaded successfully',
    uploadFailed: 'Upload failed',
    downloadedDocument: 'Downloaded {{title}}',
    failedToGenerateUrl: 'Failed to generate download URL',
    failedToDownload: 'Failed to download document',
    confirmDelete: 'Are you sure you want to delete "{{title}}"?',
    deleteSuccess: 'Document {{title}} deleted successfully',
    deleteFailed: 'Delete failed',
    failedToDelete: 'Failed to delete document',
    failedToLoad: 'Failed to load documents',
    failedToUpload: 'Failed to upload document',
  },

  // Session Timeout
  sessionTimeout: {
    title: 'Session Timeout',
    message: 'Your session is about to expire due to inactivity.',
    stayLoggedIn: 'Stay Logged In',
    logout: 'Logout',
    expiresIn: 'Session expires in {{seconds}} seconds',
  },

  // Chat Widget
  chat: {
    title: 'Chat',
    placeholder: 'Type a message...',
    send: 'Send',
    minimize: 'Minimize',
    close: 'Close Chat',
    failedToLoadPageImage: 'Failed to load page image',
  },

  // Errors
  errors: {
    generic: 'An error occurred. Please try again.',
    networkError: 'Network error. Please check your connection.',
    unauthorized: 'You are not authorized to perform this action.',
    notFound: 'The requested resource was not found.',
    serverError: 'Server error. Please try again later.',
    validationError: 'Please check your input and try again.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    failedToLoad: 'Failed to load data. Please try again.',
    failedToSave: 'Failed to save changes. Please try again.',
    failedToDelete: 'Failed to delete. Please try again.',
  },

  // Admin Tools Page
  adminToolsPage: {
    // Page header
    title: 'Admin Tools',
    subtitle: 'Administrator tools and utilities',
    backToDashboard: 'Back to Dashboard',

    // Access denied
    accessDenied: 'Access Denied',
    adminOnlyMessage: 'This page is only accessible to administrators.',
    returnToDashboard: 'Return to Dashboard',

    // Common labels
    xerAdminOnly: 'XER Admin Only',
    users: 'Users',
    close: 'Close',
    cancel: 'Cancel',
    assign: 'Assign',

    // Roles
    roles: {
      admin: 'Admin',
      adminDesc: 'Full system access',
      pilot: 'Pilot',
      pilotDesc: 'Flight operations',
      maintenance: 'Maintenance',
      maintenanceDesc: 'Maintenance operations',
      airspaceObserver: 'Airspace Observer',
      airspaceObserverDesc: 'Monitoring only',
      noRolesAssigned: 'No roles assigned',
    },

    // Metadata Extraction Card
    metadataExtraction: {
      title: 'Re-run Metadata Extraction',
      description:
        'Re-process CSV files to extract fresh metadata using the latest XER metadata extractor. This updates flight data in the database.',
      processingScope: 'Processing scope:',
      xerOnly: 'XER Technologies only',
      allOrganizations: 'ALL organizations',
      extractingFiles: 'Re-extracting metadata from CSV files... ({{current}}/{{total}} files)',
      startedBy: 'Started by: {{user}} at {{time}}',
      processingNote: 'This process may take several minutes for large collections of files.',
      processingInProgress: 'Processing in progress...',
      successMessage: 'Successfully processed {{processed}}/{{total}} CSV files',
      failedMessage: '{{message}}',
      filesFailed: '{{count}} files failed to process',
      completedIn: 'Completed in {{seconds}} seconds',
      s3Modified: 'Modified {{count}} S3 files with enhanced CSV data',
      detailedLog: 'Detailed log:',
      download: 'Download',
      failedFiles: 'Failed files:',
      andMore: '... and {{count}} more',
      jobRunning: 'Job Running...',
      processing: 'Processing...',
      startGlobalReExtraction: 'Start Global Re-extraction',
      startReExtraction: 'Start Re-extraction',
    },

    // Add Drone Card
    addDrone: {
      title: 'Add New Drone to Database',
      description:
        'Add a new drone serial number to the database. This allows flight data uploads for that drone. Drones are automatically assigned to the XER Technologies organization.',
      buttonLabel: 'Add New Drone',
      dialogTitle: 'Add New Drone to Database',
      serialNumber: 'Serial Number *',
      serialNumberPlaceholder: 'Enter drone serial number',
      model: 'Model (Optional)',
      modelPlaceholder: 'Enter drone model',
      commissioningDate: 'Commissioning Date (Optional)',
      serialRequired: 'Serial number is required',
      notes: {
        uniqueSerial: 'Serial number is required and must be unique',
        assignedToXer: 'Drone will be assigned to XER Technologies organization',
        activeByDefault: "Status will be set to 'active' by default",
      },
      adding: 'Adding...',
      addDrone: 'Add Drone',
    },

    // User Management Card
    userManagement: {
      title: 'User Management',
      description:
        'View all users in your organization and their assigned roles. Monitor user access and permissions across the platform.',
      viewUsers: 'View Users',
      inviteUser: 'Invite User',
      tableHeaders: {
        name: 'Name',
        email: 'Email',
        roles: 'Roles',
        joined: 'Joined',
        actions: 'Actions',
      },
      noUsersFound: 'No users found in this organization.',
    },

    // Organization Management Card
    orgManagement: {
      title: 'Organization Management',
      description:
        'View and manage all organizations in the system. Monitor organization settings and access across the platform.',
      viewOrganizations: 'View Organizations',
      createNewOrg: 'Create New Organization',
      unassignedUsers: 'Unassigned Users ({{count}})',
      unassignedUsersWarning:
        'These users have registered but are not assigned to any organization yet.',
      tableHeaders: {
        registered: 'Registered',
        assignToOrg: 'Assign to Organization',
        assignRoles: 'Assign Roles',
        action: 'Action',
        orgName: 'Organization Name',
      },
      selectOrganization: 'Select Organization',
      noOrgsFound: 'No organizations found in the system.',
      createOrgDialog: {
        title: 'Create New Organization',
        orgName: 'Organization Name *',
        orgNamePlaceholder: 'Enter organization name',
        slug: 'Slug: {{slug}}',
        creating: 'Creating...',
        createOrg: 'Create Organization',
      },
    },

    // Drone Assignments Card
    droneAssignments: {
      title: 'Drone Assignments',
      description:
        'Manage drone organization assignments. Reassign drones between organizations as needed for operational flexibility.',
      manageAssignments: 'Manage Assignments',
      dialogTitle: 'Drone Organization Assignments',
      tableHeaders: {
        serialNumber: 'Serial Number',
        currentOrg: 'Current Organization',
        status: 'Status',
        ownershipType: 'Ownership',
        leaseEndDate: 'Lease End Date',
        actions: 'Actions',
      },
      noOrgAssigned: 'No organization assigned',
      noOwnership: 'Not set',
      noDronesFound: 'No drones found in the system.',
    },

    // Maintenance Documents Card
    maintenanceDocs: {
      title: 'Maintenance Documents',
      description:
        'Manage maintenance documentation for the AI agent. Upload manuals, procedures, tool specifications, and diagrams for intelligent maintenance support.',
      manageDocuments: 'Manage Documents',
      dialogTitle: 'Maintenance Documents Management',
      uploadSection: 'Upload New Document',
      selectPdfFile: 'Select PDF File',
      selectedFile: 'Selected: {{name}} ({{size}} MB)',
      documentTitle: 'Document Title *',
      documentType: 'Document Type *',
      docTypes: {
        manual: 'Manual',
        procedure: 'Procedure',
        toolSpec: 'Tool Specification',
        diagram: 'Diagram',
        safety: 'Safety Document',
      },
      version: 'Version (Optional)',
      versionPlaceholder: 'v2.1, Rev 3, etc.',
      droneModel: 'Drone Model (Optional)',
      droneModelPlaceholder: 'DJI Mavic 3, etc.',
      manufacturer: 'Manufacturer (Optional)',
      manufacturerPlaceholder: 'DJI, Xer Technologies, etc.',
      uploading: 'Uploading...',
      uploadDocument: 'Upload Document',
      uploadedDocuments: 'Uploaded Documents',
      noDocsYet: 'No documents uploaded yet. Upload your first document above.',
      tableHeaders: {
        title: 'Title',
        type: 'Type',
        size: 'Size',
        status: 'Status',
        uploaded: 'Uploaded',
        actions: 'Actions',
      },
      processed: 'Processed ({{chunks}} chunks)',
      notProcessed: 'Not Processed',
      reprocessDoc: 'Reprocess document',
      processForAI: 'Process document for AI agent',
      deleteDocument: 'Delete document',
      confirmDelete: 'Are you sure you want to delete this document?\n\nKey: {{key}}',
    },

    // Tender Emails Card
    tenderEmails: {
      title: 'Tender Emails',
      description:
        'View emails forwarded to Trent (the tender agent) and their AI evaluations. Monitor tender pipeline activity and reprocess failed emails.',
      viewEmails: 'View Tender Emails',
      dialogTitle: 'Tender Email Evaluations',
      filterStatus: 'Status',
      allStatuses: 'All',
      refresh: 'Refresh',
      noEmails: 'No tender emails found.',
      tableHeaders: {
        subject: 'Subject',
        from: 'From',
        status: 'Status',
        evaluation: 'Evaluation',
        confidence: 'Confidence',
        date: 'Received',
        actions: 'Actions',
      },
      reprocess: 'Reprocess this email',
      summary: 'Summary',
      client: 'Client',
      deadline: 'Deadline',
      budget: 'Budget',
      keyFactors: 'Key Factors',
      reasoning: 'Analysis',
      replySent: 'Reply sent',
      processedAt: 'Processed',
      scrapedLinks: 'Scraped Links',
      attachments: 'Attachments',
      individualTenders: 'Individual Tenders',
      tenderTitle: 'Tender',
      pages: 'pages',
    },

    // Announcements Card
    announcements: {
      title: 'Announcements',
      description:
        'Send platform-wide announcements to users and organizations. Track delivery and read status for all sent messages.',
      manageAnnouncements: 'Manage Announcements',
      dialogTitle: 'Announcements Management',
      sendNew: 'Send New Announcement',
      announcementTitle: 'Title *',
      messageBody: 'Message Body *',
      sendTo: 'Send To *',
      targetTypes: {
        allUsers: 'All Platform Users',
        specificOrgs: 'Specific Organizations',
        specificUsers: 'Specific Users',
      },
      selectOrganizations: 'Select Organizations *',
      selectUsers: 'Select Users *',
      attachFiles: 'Attach Files (Optional)',
      sendEmailNotification: 'Also send email notification',
      sending: 'Sending...',
      sendAnnouncement: 'Send Announcement',
      sentSuccess: 'Announcement sent to {{count}} users!',
      sentAnnouncements: 'Sent Announcements ({{count}})',
      tableHeaders: {
        title: 'Title',
        created: 'Created',
        recipients: 'Recipients',
        readUnread: 'Read / Unread',
      },
      by: 'by {{name}}',
      read: '{{count}} read',
      unread: '{{count}} unread',
      noAnnouncementsYet: 'No announcements sent yet.',
    },

    // Maintenance Recalculate Card
    maintenanceRecalculate: {
      title: 'Recalculate Maintenance Counters',
      description:
        'Recalculate all maintenance counters for a drone from scratch. Rebuilds maintenance history and fixes any data inconsistencies.',
      buttonLabel: 'Recalculate Counters',
      selectDrone: 'Select Drone',
      recalculate: 'Recalculate',
      recalculating: 'Recalculating...',
      infoText: 'This will recalculate all maintenance counters from scratch. The system will:',
      infoBullet1: 'Rebuild maintenance history from all completed & quality-checked records',
      infoBullet2: 'Reset all flight and engine hour counters based on maintenance performed',
      infoBullet3: 'Fix any data inconsistencies',
      resultComplete: 'Recalculation Complete',
      resultSummary:
        'Processed {{processed}} maintenance records \u2022 {{reset}} counters updated \u2022 {{unchanged}} unchanged',
      viewDetails: 'View Details',
      hideDetails: 'Hide Details',
      totalFlightHours: 'Total Flight Hours',
      totalEngineHours: 'Total Engine Hours',
      countersBreakdown: 'Maintenance Counters Breakdown',
      tableCounter: 'Counter',
      tableLastMaintenance: 'Last Maintenance',
      tableFlightsSince: 'Flights Since',
      tableTotalHours: 'Total Hours',
      tableRemaining: 'Remaining',
      flightHoursLabel: 'Flight Hours',
      engineHoursLabel: 'Engine Hours',
      byPerformer: 'by {{name}}',
      neverMaintained: 'Never maintained',
      noFlights: 'No flights',
    },

    // Invite User Dialog
    inviteUser: {
      dialogTitle: 'Invite User to Organization',
      emailAddress: 'Email Address *',
      emailPlaceholder: 'user@example.com',
      emailHelperText: 'The user will receive an invitation link via email',
      selectRoles: 'Select Roles *',
      selectRolesHelper: 'Choose one or more roles to assign to this user',
      validation: {
        emailRequired: 'Email is required',
        invalidEmail: 'Please enter a valid email address',
        selectRole: 'Please select at least one role',
      },
      invitationNote:
        'The invitation link will expire in 24 hours. The user will be automatically assigned to your organization when they accept the invitation.',
      cancel: 'Cancel',
      sending: 'Sending...',
      sendInvitation: 'Send Invitation',
      failedToSend: 'Failed to send invitation',
    },
  },
  // User Guide
  guide: {
    title: 'User Guide',
    subtitle: 'Workflow guides for common tasks',
    backToDashboard: 'Back to Dashboard',
    headerTooltip: 'User Guide',
    common: {
      tip: 'Tip',
      important: 'Important',
    },
    sections: {
      uploadFlightData: 'Uploading & Analyzing Flight Data',
      shareFlights: "Sharing a Drone's Flights",
      maintenanceRecords: 'Maintenance Records',
      maintenanceRecordsCreating: 'Creating a Record',
      maintenanceRecordsQuality: 'Quality Checks',
      maintenanceRecordsSchedules: 'Reading Schedules',
      healthPanel: 'Reading the Health Panel',
      healthPanelAvgr: 'Understanding AVGR',
      healthPanelAvgt: 'Understanding AVGT',
      healthPanelTuwt: 'Understanding TUWT',
      healthPanelAction: 'When to Take Action',
      droneNotes: 'Drone Notes & Timeline',
      droneNotesCreating: 'Creating Notes',
      droneNotesFilters: 'Using Filters',
      droneNotesTimeline: 'Timeline Interaction',
      llmChat: 'AI Chat Assistant',
      llmChatSql: 'SQL Query Agent',
      llmChatMaintenance: 'Maintenance Agent',
      llmChatTips: 'Tips & Limits',
      inviteUsers: 'Inviting Users & Assigning Roles',
    },
    uploadFlightData: {
      intro:
        'Flight data files (CSV) require metadata before upload. This workflow walks you through uploading files and optionally analyzing them first.',
      steps: {
        s1: 'Navigate to the Files tab.',
        s2: 'Open the folder where you want to upload files.',
        s3: 'Click "Upload Files" to expand the upload zone.',
        s4: 'Drag and drop files into the upload zone, or click to browse your computer.',
        s5: 'For CSV flight data files, fill in the required metadata: flight type, payload, and pilot. These fields are mandatory for flight data.',
        s6: 'Optionally, click "Analyze" on a file to preview extracted metadata (rows, columns, detected values) before uploading.',
        s7: 'Click the upload button to upload all selected files.',
      },
      tipDocuments:
        'Document files (PDF, images) do not require flight metadata — they are uploaded directly.',
      tipAnalyze:
        'Use the "Analyze" button to validate a file before uploading. This lets you confirm the file has the expected data without committing to an upload.',
      tipPlotting:
        'To visualize and explore uploaded flight data in detail, use the "Plot Flight Data" button in the dashboard header. It opens the plotting tool where you can browse your folder structure, select a CSV file, and chart individual parameters from the flight.',
    },
    shareFlights: {
      intro:
        'Your organization can share every flight of one drone with XER Technologies in one action, for example so XER can support the aircraft. Each shared flight comes with its CSV log file and the .bin logs linked to it.',
      steps: {
        s1: 'Go to the "{{files}}" tab, open "{{sharedWithMe}}" and pick the "{{flights}}" tab.',
        s2: 'Click "{{openBulk}}".',
        s3: '{{drone}}: pick the drone\'s serial number (for example 105). {{organization}}: XER Technologies. Leave "{{fromDate}}" empty: it uses the drone\'s recorded handover date. {{access}}: "{{read}}".',
        s4: 'Click "{{share}}". A message confirms how many flights were shared.',
      },
      whatTheySee:
        'The other organization then finds these flights under "{{sharedWithMe}}", "{{flights}}", "{{inbound}}", and can open and download each flight\'s CSV log file. "{{read}}" lets them see and download the flights and nothing more: they cannot change or delete them. Shared flights do not count toward the other organization\'s own fleet totals. To stop sharing a flight, use "{{unshare}}" on it under "{{outbound}}".',
      whereTheyAppear:
        'Besides "{{sharedWithMe}}", shared flights appear on the drone\'s own pages (its profile timeline, flight list and Health panel), each marked as shared by your organization, and the drone\'s counters there say how many shared flights they include.',
      warningAdmins: 'Only organization admins see the "{{openBulk}}" button.',
      tipReshare:
        'Sharing covers the flights uploaded so far, not later ones. After you upload new flights for the drone, repeat these steps and click "{{share}}" again: only the new flights are added, and flights that are already shared are skipped.',
      tipBinLogs:
        '"{{openBulk}}" shares the drone\'s CSV flight logs, and every .bin log linked to one of those flights. When you upload a .bin log, the Dashboard looks for a flight of the same day with a matching start time and asks whether they are the same flight: answer "{{sameFlight}}" to link them. For a .bin log uploaded earlier, right-click it in the "{{files}}" tab and choose "{{linkFlight}}". A linked log follows the flight: whoever the flight is shared with can open it, and revoking the flight share withdraws it. Sharing the folder that holds the .bin logs ("{{shareFolder}}", XER Technologies under "{{folderOrganization}}", "{{folderShare}}") still works as well.',
      tipHandoverDate:
        'If the dialog says no handover date is recorded for the drone, enter the date your organization took the drone over in "{{fromDate}}" and click "{{share}}" again. Flights before that date are XER\'s own acceptance flights and are not shared.',
    },
    maintenanceRecords: {
      intro:
        'Maintenance records track work performed on a drone. Creating a record and having it quality-checked are separate steps done by different users.',
      layoutDescription:
        'The Maintenance tab has two panels: Upcoming Maintenance shows what is due or overdue based on hour intervals, and Maintenance History shows completed records.',
      creating: {
        steps: {
          s1: 'Select a drone, then navigate to the Maintenance tab.',
          s2: 'Click "Add Maintenance" to open the maintenance record form.',
          s3: 'Select one or more maintenance types. You can also create a new type on-the-fly if the one you need does not exist.',
          s4: 'Fill in the work description explaining what was done.',
          s5: 'Select who performed the maintenance.',
          s6: 'Set the start and end dates for when the work was carried out.',
          s7: 'Attach any evidence files (photos, reports) during creation.',
          s8: 'Save the record.',
        },
      },
      warningAttachments:
        'File attachments can only be added when creating the record. You cannot attach files after the record has been saved.',
      quality: {
        description:
          'After a maintenance record is created, a different user must quality-check it. The person who created the record cannot approve their own work.',
        warningDifferentUser:
          'Quality checks require a different user than the one who created the record. This is enforced by the system.',
      },
      schedules: {
        description:
          'Upcoming maintenance is based on hours-based intervals configured for each maintenance type. The panel shows how many hours remain until maintenance is due, or how many hours overdue it is. Red indicators mean the maintenance is overdue and should be performed as soon as possible.',
      },
      tipNewTypes:
        'You can create new maintenance types on-the-fly from the maintenance record form. This is useful when a new type of work is performed for the first time.',
    },
    healthPanel: {
      intro:
        'The Health Panel provides automated analysis of drone sensor data. It uses three different metric types to give you a complete picture of system health.',
      openingSteps: {
        s1: 'Select a drone from the fleet overview.',
        s2: 'Navigate to the Health Panel tab.',
        s3: 'Review the metric cards organized by system (Performance, Cooling, Batteries, etc.).',
      },
      avgr: {
        title: 'AVGR (Average Reading)',
        description:
          'AVGR shows a single value: the latest average reading for a sensor. It includes a status indicator — NORMAL (within expected range) or WARNING (outside expected range). Use this to quickly check if a sensor is currently within acceptable limits.',
      },
      avgt: {
        title: 'AVGT (Average Trend)',
        description:
          'AVGT shows a historical trend line calculated using linear regression. It includes a trend direction (IMPROVING, STABLE, or WORSENING), a forecast value for a future date, and an R² confidence score. Higher R² means the forecast is more reliable.',
        warningRSquared:
          'A low R² value (below 0.5) means the data has high variability and the forecast may not be reliable. Do not make critical decisions based on low-confidence forecasts alone.',
      },
      tuwt: {
        title: 'TUWT (Time Under Warning Threshold)',
        description:
          'TUWT shows how much time a sensor spent in warning or error state, expressed as a percentage of total monitoring time. Warning time means the sensor was outside normal range. Error time means the sensor was in a critical state.',
      },
      action: {
        description:
          'Consider taking action when: TUWT error percentage is high, AVGT shows a worsening trend with high R², or AVGR shows WARNING status consistently. Combine multiple indicators rather than relying on any single metric.',
      },
      tipDataPoints:
        'Trend analysis requires at least 3 data points. With fewer data points, the Health Panel will show the message "Insufficient data for trend analysis" instead of a forecast.',
    },
    droneNotes: {
      intro:
        'Notes let you document observations, customer feedback, and alerts for a drone. The timeline view connects notes to flight data visually.',
      creating: {
        steps: {
          s1: 'Select a drone, then navigate to the Profile tab.',
          s2: 'Click "Create Note" (or the + button) in the Notes section.',
          s3: 'Enter a subject line for the note.',
          s4: 'Select the note type: General, Customer Feedback, or Alert.',
          s5: 'Set the priority: Information or Critical.',
          s6: 'Write the note content.',
          s7: 'Save the note.',
        },
      },
      filters: {
        description:
          'Use the filter controls to narrow down the notes list. You can filter by type (General, Customer Feedback, Alert), priority (Information, Critical), and whether resolved notes are included.',
      },
      timeline: {
        description:
          'The timeline at the top of the Profile tab shows a Gantt chart of flight data. Clicking a flight bar in the timeline scrolls down to and highlights the corresponding note below. This helps you connect specific flights to related observations.',
        tipDateRange:
          'The date range slider filters the notes list but does not affect the timeline view. The timeline always shows all available data.',
      },
    },
    llmChat: {
      intro:
        'The platform includes an AI chat assistant accessible via the floating button in the bottom-right corner of the dashboard. You choose an agent, then ask questions in natural language.',
      steps: {
        s1: 'Click the chat bubble in the bottom-right corner of the screen.',
        s2: 'Select an agent: SQL Query Agent for data questions, or Maintenance Agent for technical documentation.',
        s3: 'Type your question or click one of the example queries shown.',
        s4: 'Review the response. For SQL queries, you can expand "View SQL" to see the exact query that was executed.',
      },
      sql: {
        title: 'SQL Query Agent',
        description:
          'Ask questions about your fleet data in plain language. The agent converts your question into a SQL query, runs it against the database, and returns the results in readable form. You only see data from your own organization — this is enforced automatically.',
        examples:
          'Example questions: "How many flights under 10 minutes for drone 102?", "What is the total flight time this month?", "Show me drones that need maintenance", "How many unresolved notes are there?"',
      },
      maintenance: {
        title: 'Maintenance Agent',
        description:
          'Ask questions about maintenance procedures, safety protocols, and technical specifications. The agent searches uploaded maintenance documentation (manuals, procedures, tool specs) and synthesizes an answer with source citations. Answers reference specific document sections and page numbers.',
        examples:
          'Example questions: "How do I replace the propellers?", "What are the safety protocols for battery maintenance?", "Troubleshooting steps for motor failure", "What tools do I need for routine inspection?"',
      },
      tips: {
        rateLimit:
          'You are limited to 20 queries per hour. The limit resets automatically. If you reach it, wait and try again later.',
        feedback:
          'Use the thumbs up/down buttons on responses to provide feedback. This helps improve the quality of answers over time. Thumbs down lets you add an optional comment explaining what was wrong.',
        privacy:
          'All queries are scoped to your organization. The SQL agent can only read data — it cannot modify, delete, or create anything in the database.',
        session:
          'The chat remembers your conversation within a session. Click the refresh icon in the chat header to start a new session and switch agents.',
      },
    },
    inviteUsers: {
      intro:
        'When you invite a user, they receive an email link. Once they accept and create their account, they are automatically assigned to your organization with the roles you selected.',
      steps: {
        s1: 'Navigate to Admin Tools.',
        s2: 'In the User Management section, click "Invite User".',
        s3: "Enter the user's email address and select one or more roles.",
        s4: 'Click "Send Invitation". The user receives an email with a link to create their account.',
        s5: 'When the user clicks the link and creates their password, they are automatically assigned to your organization with the selected roles.',
        s6: 'The user can now sign in and access the system immediately.',
      },
      warningExpiry:
        'Invitation links expire in 24 hours. If the link expires, you need to send a new invitation.',
      warningUnassigned:
        'The "Unassigned Users" list in Organization Management shows users who registered on their own (without an invitation). These users must be manually assigned to an organization by an admin before they can access the system.',
    },
  },

  // Inventory
  inventory: {
    title: 'Inventory',
    locations: 'Locations',
    items: 'Items',
    allItems: 'All Items',
    addLocation: 'Add Location',
    addItem: 'Add Item',
    editLocation: 'Edit Location',
    editItem: 'Edit Item',
    deleteLocation: 'Delete Location',
    deleteItem: 'Delete Item',
    confirmDeleteLocation:
      'Are you sure you want to delete this location? All child locations will also be deleted. Items in deleted locations will become unassigned.',
    confirmDeleteItem: 'Are you sure you want to delete this item?',
    name: 'Name',
    locationType: 'Type',
    parentLocation: 'Parent Location',
    notes: 'Notes',
    quantity: 'Quantity',
    category: 'Category',
    location: 'Location',
    search: 'Search items...',
    noLocations: 'No locations yet. Add your first location to get started.',
    noItems: 'No items found.',
    noItemsInLocation: 'No items in this location.',
    sortOrder: 'Sort Order',
    locationTypes: {
      area: 'Area',
      shelf: 'Shelf',
      level: 'Level',
      box: 'Box',
      pelicase: 'Pelicase',
      bag: 'Bag',
      section: 'Section',
      bucket: 'Bucket',
      container: 'Container',
    },
    actions: 'Actions',
    searchLocations: 'Search locations...',
    locationPath: 'View location path',
    backTo: 'Back to {{name}}',
    rootLevel: 'Root',
    noSelection: 'None selected',
  },

  // Operations Equipment
  operationsEquipment: {
    title: 'Operations Equipment',
    add: 'Add Equipment',
    addTitle: 'Add Equipment',
    edit: 'Edit',
    editTitle: 'Edit Equipment',
    delete: 'Delete Equipment',
    confirmDelete: 'Are you sure you want to delete this equipment?',
    name: 'Name',
    location: 'Location',
    quantity: 'Quantity',
    drone: 'Drone',
    photo: 'Photo',
    photoUrl: 'Photo URL',
    notes: 'Notes',
    actions: 'Actions',
    noEquipment: 'No operations equipment yet. Add your first piece of equipment to get started.',
  },

  // Manufacturing (STD-015 query/mutation error fallbacks)
  manufacturing: {
    failedToCheckAccess: 'Failed to check manufacturing access',
    failedToLoad: 'Failed to load manufacturing data. Check that the snapshot scripts have run.',
    failedToLoadPartDetail: 'Failed to load part detail',
    failedToSimulate: 'Simulation failed. Confirm the product has a BOM and try again.',
  },

  // Flight sharing (#1182 Stage 3) — explicit, revocable per-flight grants.
  //
  // What is left is the BULK drone-serial dialog, plus the error copy it shares
  // with the Shared view. The per-file "Sharing..." dialog's own strings — its
  // title, current-access list, revoke prompts and grant form — retired with
  // the dialog itself in B-6 (STD-029 R-12): the file browser has one Share
  // action now and it speaks `objectShares.*`, while withdrawing a live flight
  // grant happens on the Shared view, under `sharedView.*`.
  flightShares: {
    selectOrganization: 'Organization',
    permission: 'Access',
    permissionRead: 'Read',
    permissionWrite: 'Read + edit details',
    permissionHelp:
      "Read lets the other organization see and download the flights. Read + edit details also lets them edit each flight's details (type, pilot, payload, customer, project). Only your organization can delete a flight.",
    sharedOne: 'Shared 1 flight.',
    sharedOther: 'Shared {{count}} flights.',
    skippedOne: '1 was already shared.',
    skippedOther: '{{count}} were already shared.',
    nothingNewToShare: 'Every matching flight was already shared - nothing to do.',
    droneOwnedBy: 'Owned by {{org}}.',
    droneFlightsOne: '1 flight recorded by your organization on it.',
    droneFlightsOther: '{{count}} flights recorded by your organization on it.',
    noShareableDrones: 'Your organization has no recorded flights to share yet.',
    onlyAdmins: 'Only organization admins can change sharing.',
    bulkTitle: "Share a drone's flights",
    bulkIntro:
      'Shares every flight your organization recorded for this drone in one action, rather than one flight at a time. Safe to re-run: flights that are already shared are skipped.',
    drone: 'Drone',
    fromDate: 'From date',
    toDate: 'To date',
    fromDateHelp: "Leave empty to use the drone's recorded handover date.",
    bulkShare: 'Share flights',
    // Direction-neutral on purpose: the same string is used under "Shared by
    // us" and "Shared with us", where "with" and "by" would swap.
    errors: {
      noSoldDate:
        'No handover date is recorded for drone {{serial}}, so there is no defensible cut-off between XER\'s own pre-handover flights and the customer\'s. Enter the date the customer took the aircraft over in "From date" below.',
      noFlights: 'No flights of your organization matched - nothing was shared.',
      illegalPairing:
        'Flights can only be shared between XER Technologies and a customer organization, in either direction, never between two customers.',
    },
  },

  objectShares: {
    menuItemFile: 'Share file...',
    menuItemFolder: 'Share folder...',
    menuItemMany: 'Share {{count}} items...',

    dialogTitle: 'Sharing - {{name}}',
    dialogTitleMany: 'Sharing {{count}} items',
    multiSelection:
      'Sharing all {{count}} selected items. Existing shares are kept - anything already shared with the organization you pick is skipped.',
    folderCoversFutureFiles:
      'Sharing a folder also shares everything inside it, including files added later. You do not need to share each new file.',
    currentShares: 'Current access',
    noShares: 'This is not shared with anyone.',
    sharedWithOrg: 'Shared with {{org}}',
    sharedByOrg: 'Shared with us by {{org}}',
    sharedDirectionUnknown: 'Shared (recipient unavailable)',
    unknownOrg: 'Another organization',
    grantedOn: 'Granted {{date}}',

    revoke: 'Unshare',
    revokeConfirm: 'Stop sharing this file with {{org}}? Access stops immediately.',
    revokeConfirmFolder:
      'Stop sharing this folder with {{org}}? They lose access to everything inside it, including files added since it was shared.',
    revokeSuccess: 'Sharing stopped.',

    shareWith: 'Share with',
    selectOrganization: 'Organization',
    permission: 'Access',
    permissionRead: 'Read',
    permissionWrite: 'Read + write',
    permissionHelp:
      'Read lets the other organization see and download this file. Read + write also lets them rename it. Only your organization can delete it.',
    permissionHelpFolder:
      'Read lets the other organization see and download everything in this folder, including files added later. Read + write also lets them create folders and rename or move items inside it. They cannot yet upload into it from the Files tab, because such uploads would not be processed as flights. Only your organization can delete anything in it.',
    share: 'Share',
    sharedOne: 'Shared 1 item.',
    sharedOther: 'Shared {{count}} items.',
    skippedOne: '1 was already shared.',
    skippedOther: '{{count}} were already shared.',
    noCounterparties: 'There is no organization you can share this with.',
    onlyAdmins: 'Only organization admins can change sharing.',

    errors: {
      noObjects:
        'None of the selected items belong to your organization, so there was nothing to share.',
      illegalPairing:
        'Files can only be shared between XER Technologies and a customer organization, in either direction, never between two customers.',
    },
  },

  // Linking a .bin log to its flight record (#1694). Linking to a shared flight
  // hands the log to that flight's grantees, so the dialog says who first.
  flightLinks: {
    menuItem: 'Link to flight...',
    dialogTitle: 'Link to flight - {{name}}',
    intro:
      'A .bin log linked to a flight travels with it: every organization the flight is shared with can open and plot the log. Unlinking it, or revoking the flight share, withdraws it again.',
    current: 'Linked to',
    noLinks: 'This log is not linked to any flight.',
    candidates: 'Flights this log may belong to',
    day: 'Day',
    logStart: 'Log started {{time}} (from the file name).',
    noLogStart:
      'The file name holds no start time, so no flight is marked. Pick the day the log was recorded.',
    noCandidates:
      "No flight of the log's organization that you can see on that day. Pick another day.",
    proposed: 'Likely match',
    deltaLogName: '{{minutes}} min from the CSV name',
    deltaFirstSample: '{{minutes}} min from the first sample (UTC)',
    flightOn: 'Drone {{serial}}, {{date}}',
    sharedWith: 'Shared with {{orgs}}: linking gives them this log',
    sharedWithLinked: 'Shared with {{orgs}}, who can open this log',
    notShared: 'Flight not shared',
    unknownOrg: 'Another organization',
    link: 'Link',
    unlink: 'Unlink',
    unlinkConfirm:
      'Unlink this log from the flight? Organizations the flight is shared with lose access to the log at once.',
    linked: 'Linked.',
    alreadyLinked: 'Already linked.',
    unlinked: 'Unlinked.',
    promptTitle: 'Is this the same flight?',
    promptQuestion: '{{bin}} and {{flight}} started at nearly the same time.',
    promptNo: 'No',
    promptYes: 'Yes, these are the same flight',
  },

  // The INBOUND file/folder listing (#1205) — what other organizations shared
  // with yours. Its own namespace rather than keys under `objectShares`, which
  // is about granting: this screen is read-only and its audience is every
  // member, not just admins.
  sharedView: {
    navLabel: 'Shared with me',
    title: 'Shared with me',
    backToFiles: 'Back to Files',
    kindFlights: 'Flights',
    kindFiles: 'Files',
    flightsIntro: 'Flights shared between your organization and others.',
    flightsIntroDetail:
      'A shared flight carries its log file, and any .bin logs linked to it, so they open like any other file. What your organization shares is listed under "Shared by us"; what others share with you, under "Shared with us".',
    filesIntro: 'Files and folders shared between your organization and others.',
    filesIntroDetail:
      'Sharing a folder shares everything inside it, including files added later - so items can appear here without anyone sharing them individually.',
    aboutThisPage: 'About this page',

    outbound: 'Shared by us',
    inbound: 'Shared with us',
    allShares: 'All shares',
    filterCounterparty: 'Organization',
    allCounterparties: 'All organizations',
    unknownOrg: 'Another organization',
    includeRevoked: 'Include revoked',
    openBulk: "Share a drone's flights",
    onlyAdmins: 'Only organization admins can change sharing.',

    permissionRead: 'Read',
    permissionWrite: 'Read + write',
    droneChip: 'Drone {{serial}}',
    flightOn: 'Flight {{date}}',
    trackLabel: 'DroneLogbook track · {{date}}',
    trackLabelUndated: 'DroneLogbook track',
    trackTooltip: 'A flight record from DroneLogbook, without a log file.',
    grantedOn: 'Granted {{date}}',
    revokedOn: 'Revoked {{date}}',
    unnamedEntry: 'File unavailable',

    openFolder: 'Open folder',
    download: 'Download',
    downloadStarted: 'Download started.',
    plot: 'Open in Analytics',
    revoke: 'Unshare',
    revokeConfirm:
      'Stop sharing this with {{org}}? Access stops immediately; anything already derived from it stays valid.',
    revokeSuccess: 'Sharing stopped.',

    rowsPerPage: 'Items per page',
    paginationRange: '{{from}}-{{to}} of {{count}}',
    noneShared: 'Nothing here yet.',
    noneForOrg: 'No shares with this organization.',
    emptyOutboundFlights:
      "No flights are currently shared by your organization. An organization admin can share a drone's flights from this page.",
    emptyOutboundFiles:
      'No files or folders are currently shared by your organization. Share one from its menu in Files.',
    emptyInboundFlights:
      'No organization is currently sharing flights with yours. Flights shared with you appear here, with their log files.',
    emptyInboundFiles: 'No organization is currently sharing files or folders with yours.',
  },
} as const;
